import { useEffect, useRef, useState } from 'react';
import { normalizeImageFile } from '../utils/heic';

interface Props {
  label: string;
  subLabel: string;
  max: number;
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
  emptyTitle?: string;
  emptyHint?: string;
}

/**
 * MultiUpload — gallery dropzone (up to `max` images) styled to match
 * small-family-with-pet-and-caption.html: a white bounding card with an
 * empty-state (large icon + plus badge + privacy lock line), and a thumbnail
 * gallery with numbered slots, a red remove button, and an add tile.
 */

const GOLD = '#a8763e';
const BORDER = '#d6cfc5';
const INK = '#4a4036';
const MUTE = '#8c8072';

export function MultiUpload({ label, subLabel, max, files, onChange, disabled, emptyTitle, emptyHint }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [urls, setUrls] = useState<string[]>([]);
  useEffect(() => {
    const next = files.map((f) => URL.createObjectURL(f));
    setUrls(next);
    return () => next.forEach((u) => URL.revokeObjectURL(u));
  }, [files]);

  const [converting, setConverting] = useState(false);
  const pick = () => {
    if (disabled || converting || files.length >= max) return;
    inputRef.current?.click();
  };
  const onFiles = async (list: FileList | null) => {
    if (!list || list.length === 0) return;
    // image/* wildcard already covers image/heic on most platforms; also
    // catch Safari's empty-type HEIC files by extension.
    const candidates = Array.from(list).filter(
      (f) => f.type.startsWith('image/') || /\.hei[cf]$/i.test(f.name),
    );
    if (candidates.length === 0) return;
    setConverting(true);
    try {
      const incoming = await Promise.all(candidates.map(normalizeImageFile));
      onChange([...files, ...incoming].slice(0, max));
    } finally {
      setConverting(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };
  const removeAt = (i: number) => onChange(files.filter((_, idx) => idx !== i));

  const empty = files.length === 0;
  const full = files.length >= max;

  return (
    <section className="flex flex-col" style={{ gap: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: '#6a5d4d' }}>
        {label}{' '}
        <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: MUTE, fontSize: 11 }}>
          ({subLabel})
        </span>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        disabled={disabled}
        onChange={(e) => onFiles(e.target.files)}
      />

      <div
        onClick={empty ? pick : undefined}
        style={{
          border: `1px solid ${BORDER}`,
          borderRadius: 16,
          background: '#fff',
          boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
          padding: empty ? '30px 20px' : 20,
          minHeight: empty ? 220 : 'auto',
          display: 'flex',
          flexDirection: empty ? 'column' : 'row',
          flexWrap: 'wrap',
          gap: empty ? 0 : 15,
          alignItems: empty ? 'center' : 'flex-start',
          justifyContent: empty ? 'center' : 'flex-start',
          cursor: empty && !disabled ? 'pointer' : 'default',
          transition: 'border-color .2s',
        }}
      >
        {empty ? (
          <>
            <div style={{ position: 'relative', width: 56, height: 56, border: `1.5px solid ${GOLD}`, borderRadius: 14, background: '#faf7f2', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              <span style={{ position: 'absolute', top: -8, right: -8, width: 20, height: 20, background: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ width: 16, height: 16, background: GOLD, color: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, lineHeight: 1 }}>+</span>
              </span>
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: INK, marginBottom: 8 }}>{emptyTitle ?? `Add ${label.toLowerCase()}`}</div>
            <div style={{ fontSize: 13, color: MUTE, marginBottom: 20 }}>{emptyHint ?? subLabel}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#a89d8f' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19 11H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2zm-7 7.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5zM17 11H7V7a5 5 0 0 1 10 0v4z"/></svg>
              Your photos stay private
            </div>
          </>
        ) : (
          <>
            {files.map((f, i) => (
              <div key={`${f.name}-${i}`} style={{ position: 'relative', width: 85, height: 85, borderRadius: 16, background: '#fff', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}>
                <img src={urls[i]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 16, display: 'block' }} />
                <span style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '50%', background: 'linear-gradient(transparent, rgba(0,0,0,0.7))', borderRadius: '0 0 16px 16px', pointerEvents: 'none' }} />
                <span style={{ position: 'absolute', bottom: 6, left: 10, color: '#fff', fontWeight: 700, fontSize: 14 }}>#{i + 1}</span>
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  disabled={disabled}
                  aria-label="Remove photo"
                  style={{ position: 'absolute', top: -6, right: -6, width: 22, height: 22, background: '#dc3545', color: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, border: 'none', boxShadow: '0 2px 5px rgba(0,0,0,0.2)', cursor: 'pointer', zIndex: 10, lineHeight: 0 }}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
                </button>
              </div>
            ))}
            {!full && (
              <button
                type="button"
                onClick={pick}
                disabled={disabled || converting}
                aria-label={`Add to ${label}`}
                style={{ width: 85, height: 85, borderRadius: 16, border: `1.5px solid ${BORDER}`, background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all .2s' }}
              >
                {converting ? (
                  <span style={{ fontSize: 10, color: MUTE }}>Converting…</span>
                ) : (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.75 }}><path d="M21 11.5v4.5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8.5"/><circle cx="9" cy="9" r="2"/><path d="M21 15l-5-5L5 21"/><line x1="19" y1="3" x2="19" y2="9"/><line x1="16" y1="6" x2="22" y2="6"/></svg>
                )}
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
}
